// Shapes the proctoring payload (getProctoringDetails) for the student report.
//
// One attempt can hold MORE THAN ONE proctoring session: the candidate
// reconnected, or — the common case — the webcam capture taken as they hit
// submit reached the server after the session was closed and opened a session
// of its own holding that single snapshot. admin-node returns sessions
// newest-first, so reading `sessions[0]` rendered exactly that stray image and
// hid the 60-250 real ones. student-node no longer creates those stray sessions,
// but attempts already recorded still carry them, so the report reads across
// every session.
//
// See proctoringView.test.js.

// Every snapshot of the attempt, oldest capture first.
const collectSnapshots = (sessions = []) =>
  sessions
    .flatMap(session => session?.snapshots || [])
    .sort((a, b) => new Date(a.capturedAt) - new Date(b.capturedAt))

// The session the candidate actually sat in — the richest one, never a
// straggler that holds a lone post-submit snapshot.
const primarySession = (sessions = []) =>
  sessions.reduce(
    (longest, session) =>
      (session?.snapshots?.length || 0) > (longest?.snapshots?.length || 0)
        ? session
        : longest,
    sessions[0] || {}
  )

// IPs seen across the whole attempt.
const collectIpAddresses = (sessions = []) =>
  sessions.flatMap(session => session?.ipAddresses || [])

// Timeline event types that mean "the candidate read the answer", as the
// scorer writes them. Shown as "Reading detected", like the corporate drawer.
const READING_EVENT_TYPES = ['reading_pace_suspected', 'external_reference_suspected']

const isReadingEvent = event => READING_EVENT_TYPES.includes(event?.type)

// "Reading detected", counted exactly as corporate-react-v2's drawer: answers
// delivered at read-aloud pace (readingPaceSuspectedCount, else the confirmed
// readingFlaggedQuestionIds) plus suspected reading from another device
// (externalReferenceSuspectedCount). Null when the report carries neither —
// omitted rather than shown as a zero the engine never measured.
const readingDetectedCount = (summary = {}) => {
  const pace = typeof summary.readingPaceSuspectedCount === 'number'
    ? summary.readingPaceSuspectedCount
    : Array.isArray(summary.readingFlaggedQuestionIds)
      ? summary.readingFlaggedQuestionIds.length
      : null
  const external = typeof summary.externalReferenceSuspectedCount === 'number'
    ? summary.externalReferenceSuspectedCount
    : null
  return pace === null && external === null ? null : (pace ?? 0) + (external ?? 0)
}

module.exports = {
  collectSnapshots,
  primarySession,
  collectIpAddresses,
  isReadingEvent,
  readingDetectedCount,
}
